// Global client-side error reporter — POSTs unhandled JS errors to the
// backend `/api/system-debug/report` endpoint so admins can triage them
// from the "System Debug" page without asking users for F12 output.
//
// Kept intentionally tiny and fail-silent so a broken reporter never
// blocks page load or masks the original error in the console.

const REPORT_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api/system-debug/report';
const RATE_LIMIT_MS = 3000; // Don't spam the endpoint if the same error fires in a loop
const seen = new Map();     // key -> lastSentTs

function _shouldSend(key) {
  const now = Date.now();
  const last = seen.get(key) || 0;
  if (now - last < RATE_LIMIT_MS) return false;
  seen.set(key, now);
  return true;
}

async function _send(payload) {
  try {
    const token = localStorage.getItem('token');
    await fetch(REPORT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true, // best-effort on page unload
    });
  } catch { /* silent */ }
}

export function installClientErrorReporter() {
  if (typeof window === 'undefined') return;
  if (window.__darasaniErrorReporterInstalled) return;
  window.__darasaniErrorReporterInstalled = true;

  window.addEventListener('error', (evt) => {
    const err = evt?.error || {};
    const key = `${err?.name || 'Error'}:${evt?.message}:${evt?.filename}:${evt?.lineno}`;
    if (!_shouldSend(key)) return;
    _send({
      kind: err?.name || 'JSError',
      message: String(evt?.message || err?.message || 'Unknown error').slice(0, 2000),
      severity: 'error',
      source: 'frontend',
      stack: (err?.stack || '').slice(0, 6000),
      context: { filename: evt?.filename, line: evt?.lineno, col: evt?.colno, ua: navigator.userAgent },
      url: window.location.href,
    });
  });

  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt?.reason || {};
    const message = typeof reason === 'string' ? reason : (reason?.message || 'Unhandled promise rejection');
    const key = `Rejection:${message}`;
    if (!_shouldSend(key)) return;
    _send({
      kind: reason?.name || 'UnhandledPromiseRejection',
      message: String(message).slice(0, 2000),
      severity: 'error',
      source: 'frontend',
      stack: (reason?.stack || '').slice(0, 6000),
      context: { ua: navigator.userAgent },
      url: window.location.href,
    });
  });
}
